import { Switch, Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import ChipInput from 'material-ui-chip-input';
import React, { JSX, useState } from 'react';
import update from 'react-addons-update';

import {
  AudienceSegmentThirdPartyPartnerCadent,
  AudienceSegmentThirdPartyPartnerLiveRamp,
  AudienceSegmentThirdPartyPartnerTransUnion,
  IPublisher,
  updatePublisher,
} from '~/src/dash/api/console-api';
import { EBool } from '~/src/dash/api/types/bool';
import { IAccordionPanel, MultiAccordion } from '~/src/dash/components/accordion/multi-accordion';
import ConfirmationDialogButton from '~/src/dash/components/confirmation-dialog-button';
import { FormBuilderTextInput } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/FormBuilderTextInput';
import { IInputRow } from '~/src/dash/components/input-row';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Col, Cols, Div, Panel, Panels, Row, Text } from '~/src/dash/publicaUI';
import { SESSION_ACTIONS_SET } from '~/src/dash/reducers/session-reducers';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  navigate,
  RouteAudienceManagerSettingsList,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { routeName } from '~/src/dash/utils/routes';
import { ButtonView } from '~/src/dash/views/button-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

export const AudienceManagerDataProviderSettings = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  function (props: IProps) {
    const { session, location, user, classes } = props;
    const { pathname } = location;
    const { publisher } = session;
    // publisher thirdparty enabled options
    const [liverampEnabled, setLiverampEnabled] = useState<EBool>(publisher.LiverampEnabled);
    const [cadentEnabled, setCadentEnabled] = useState<EBool>(publisher.CadentEnabled);
    const [transunionEnabled, setTransunionEnabled] = useState<EBool>(publisher.TransunionEnabled);
    const [samsungDMPEnabled, setSamsungDMPEnabled] = useState<EBool>(publisher.SamsungDMPEnabled);
    const [onePlusXDMPEnabled, setOnePlusXEnabled] = useState<EBool>(publisher.OnePlusXDMPEnabled);

    // publisher opt in options (whether to drop a pixel to partner endpoint)
    const [optInLiveramp, setOptInLiveramp] = useState<EBool>(publisher.OptInLiveramp);
    const [optInCadent, setOptInCadent] = useState<EBool>(publisher.OptInCadent);
    const [optInTransunion, setOptInTransunion] = useState<EBool>(publisher.OptInTransunion);

    // publisher opt in sharing data options (whether sharing auction level aggregation data to partner)
    const [optInSharingDataLiverampOTTHub, setOptInSharingDataLiverampOTTHub] = useState<EBool>(
      publisher.OptInSharingDataLiveramp,
    );
    // Inactive On the UI
    // OptInSharingDataCadent
    // OptInSharingDataTransunion

    // publisher opt in storing data options (whether storing aggregation data internally)
    const [optInStoringDataLiveramp, setOptInStoringDataLiveramp] = useState<EBool>(
      publisher.OptInStoringDataLiveramp,
    );
    const [optInStoringDataCadent, setOptInStoringDataCadent] = useState<EBool>(
      publisher.OptInStoringDataCadent,
    );
    const [optInStoringDataTransunion, setOptInStoringDataTransunion] = useState<EBool>(
      publisher.OptInStoringDataTransunion,
    );

    // fields related to liveramp
    const [liverampClientNames, setLiverampClientNames] = useState<string[]>(
      publisher.LiverampClientNames === ''
        ? ([] as string[])
        : publisher.LiverampClientNames.split(','),
    );
    const [liverampClientNamesInput, setLiverampClientNamesInput] = useState<string>('');
    const [liverampClientNamesFirstParty, setLiverampClientNamesFirstParty] = useState<string[]>(
      publisher.LiverampClientNamesFirstParty === ''
        ? ([] as string[])
        : publisher.LiverampClientNamesFirstParty.split(','),
    );
    const [liverampClientNamesFirstPartyInput, setLiverampClientNamesFirstPartyInput] =
      useState<string>('');
    const [liverampOTTHubCustomerID, setLiverampOTTHubCustomerID] = useState<string>(
      publisher.LiverampOTTHubCustomerID,
    );

    const [liverampDistributionManagerID, setLiverampDistributionManagerID] = useState<string>(
      publisher.LiverampDistributionManagerID || '',
    );

    // fields related to cadent
    const [cadentTag, setCadentTag] = useState<string[]>(
      publisher.CadentTag === '' ? ([] as string[]) : publisher.CadentTag.split(','),
    );
    const [cadentTagInput, setCadentTagInput] = useState<string>('');

    // fields related to transunion
    const [transunionAPIKey, setTransunionAPIKey] = useState<string>(publisher.TransunionAPIKey);

    const [updatingPublisher, setUpdatingPublisher] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [snackbarError, setSnackbarError] = useState<boolean>(false);

    const gotoAudienceManagerSettings = () => {
      navigate(
        RouteAudienceManagerSettingsList,
        extractRouteParams({
          session,
          user,
        }),
      );
    };

    const handleSave = () => {
      if (liverampEnabled === EBool.EBool_TRUE && liverampDistributionManagerID === '') {
        setSnackbarMessage('Please enter Liveramp Distribution Manager ID');
        setSnackbarError(true);
        return;
      }
      let updates: { [key: string]: any } = {};
      updates = {
        LiverampEnabled: liverampEnabled,
        CadentEnabled: cadentEnabled,
        TransunionEnabled: transunionEnabled,
        SamsungDMPEnabled: samsungDMPEnabled,
        OnePlusXDMPEnabled: onePlusXDMPEnabled,

        OptInLiveramp: optInLiveramp,
        OptInCadent: optInCadent,
        OptInTransunion: optInTransunion,

        OptInSharingDataLiveramp: optInSharingDataLiverampOTTHub,

        OptInStoringDataLiveramp: optInStoringDataLiveramp,
        OptInStoringDataCadent: optInStoringDataCadent,
        OptInStoringDataTransunion: optInStoringDataTransunion,

        liverampClientNames: liverampClientNames.join(','),
        LiverampClientNamesFirstParty: liverampClientNamesFirstParty.join(','),
        LiverampOTTHubCustomerID: liverampOTTHubCustomerID,
        LiverampDistributionID:
          liverampEnabled === EBool.EBool_TRUE ? liverampDistributionManagerID : '',

        CadentTag: cadentTag.join(','),

        TransunionAPIKey: transunionAPIKey,
      };

      updatePublisher({
        query: {
          publisher: Object.assign(
            {
              ID: session.publisher.ID,
            },
            updates,
          ),
        },
      }).then(
        ({ publisher }: { publisher: IPublisher }) => {
          props.dispatch(
            SESSION_ACTIONS_SET({
              publisher,
            }),
          );
          setSnackbarMessage('Successfully updated publisher settings.');
          setSnackbarError(false);
          setUpdatingPublisher(false);
        },
        () => {
          setSnackbarMessage('Updating publisher settings failed, please try again later.');
          setSnackbarError(true);
          setUpdatingPublisher(false);
        },
      );
    };

    const handleChipInputAddFirstParty = (value: string) => {
      setLiverampClientNamesFirstPartyInput('');
      setUpdatingPublisher(true);
      setLiverampClientNamesFirstParty(
        update(liverampClientNamesFirstParty, {
          $push: [value],
        }),
      );
    };

    const handleChipInputDeleteFirstParty = (_: any, idx: number) => {
      setUpdatingPublisher(true);
      setLiverampClientNamesFirstParty(
        update(liverampClientNamesFirstParty, {
          $splice: [[idx, 1]],
        }),
      );
    };

    const handleChipInputUpdateInputFirstParty = ({
      target,
    }: React.ChangeEvent<HTMLInputElement>) => {
      setUpdatingPublisher(true);
      if (target.value.indexOf(' ') > -1 || target.value.indexOf(',') > -1) {
        if (target.value.length === 1) {
          setLiverampClientNamesFirstPartyInput('');
        } else {
          setLiverampClientNamesFirstPartyInput('');
          setLiverampClientNamesFirstParty(
            update(liverampClientNamesFirstParty, {
              $push: [target.value.substring(0, target.value.length - 1)],
            }),
          );
        }
      } else {
        setLiverampClientNamesFirstPartyInput(target.value);
      }
    };

    const handleChipInputAdd = (value: string) => {
      setUpdatingPublisher(true);
      setLiverampClientNamesInput('');
      setLiverampClientNames(
        update(liverampClientNames, {
          $push: [value],
        }),
      );
    };

    const handleChipInputDelete = (_: any, idx: number) => {
      setUpdatingPublisher(true);
      setLiverampClientNames(
        update(liverampClientNames, {
          $splice: [[idx, 1]],
        }),
      );
    };

    const handleChipInputUpdateInput = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      setUpdatingPublisher(true);
      if (target.value.indexOf(' ') > -1 || target.value.indexOf(',') > -1) {
        if (target.value.length === 1) {
          setLiverampClientNamesInput('');
        } else {
          setLiverampClientNamesInput('');
          setLiverampClientNames(
            update(liverampClientNames, {
              $push: [target.value.substring(0, target.value.length - 1)],
            }),
          );
        }
      } else {
        setLiverampClientNamesInput(target.value);
      }
    };

    const handleLiverampOTTHubCustomerIDChange = (value: string): void => {
      setUpdatingPublisher(true);
      setLiverampOTTHubCustomerID(value);
    };

    const handleLiverampDistributionIDChange = (value: string): void => {
      setUpdatingPublisher(true);
      setLiverampDistributionManagerID(value);
    };

    const handleChipInputAddCadentTag = (value: string) => {
      setUpdatingPublisher(true);
      setCadentTagInput('');
      setCadentTag(
        update(cadentTag, {
          $push: [value],
        }),
      );
    };

    const handleChipInputDeleteCadentTag = (_: any, idx: number) => {
      setUpdatingPublisher(true);
      setCadentTag(
        update(cadentTag, {
          $splice: [[idx, 1]],
        }),
      );
    };

    const handleChipInputUpdateInputCadentTag = ({
      target,
    }: React.ChangeEvent<HTMLInputElement>) => {
      setUpdatingPublisher(true);
      if (target.value.indexOf(' ') > -1 || target.value.indexOf(',') > -1) {
        if (target.value.length === 1) {
          setCadentTagInput('');
        } else {
          setCadentTagInput('');
          setCadentTag(
            update(cadentTag, {
              $push: [target.value.substring(0, target.value.length - 1)],
            }),
          );
        }
      } else {
        setCadentTagInput(target.value);
      }
    };

    const handleChangeDataShareToggle = (partner: string, isDataShare: boolean) => {
      setUpdatingPublisher(true);
      if (partner === AudienceSegmentThirdPartyPartnerLiveRamp) {
        isDataShare
          ? setOptInLiveramp(EBool.reverse(optInLiveramp))
          : setOptInStoringDataLiveramp(EBool.reverse(optInStoringDataLiveramp));
      } else if (partner === AudienceSegmentThirdPartyPartnerCadent) {
        isDataShare
          ? setOptInCadent(EBool.reverse(optInCadent))
          : setOptInStoringDataCadent(EBool.reverse(optInStoringDataCadent));
      } else {
        isDataShare
          ? setOptInTransunion(EBool.reverse(optInTransunion))
          : setOptInStoringDataTransunion(EBool.reverse(optInStoringDataTransunion));
      }
    };

    const snackbarClose = () => {
      setSnackbarMessage('');
    };

    const customRenderFirstPartyClientNames = () => {
      return (
        <div style={{ margin: '20px 0px 20px 0px', width: '800px' }}>
          <Text title>LiveRamp Settings * </Text>
          <Text muted extraSmall>
            Enter details for Liveramp Settings{' '}
          </Text>
          <ChipInput
            fullWidth
            value={liverampClientNamesFirstParty}
            inputValue={liverampClientNamesFirstPartyInput}
            onAdd={handleChipInputAddFirstParty}
            onDelete={handleChipInputDeleteFirstParty}
            onUpdateInput={handleChipInputUpdateInputFirstParty}
            style={{ marginTop: '10px' }}
          />
          <Text small>First Party Client Names</Text>
        </div>
      );
    };

    const customRenderThirdPartyClientNames = () => {
      return (
        <div style={{ marginBottom: '20px', width: '800px' }}>
          <ChipInput
            fullWidth
            value={liverampClientNames}
            inputValue={liverampClientNamesInput}
            onAdd={handleChipInputAdd}
            onDelete={handleChipInputDelete}
            onUpdateInput={handleChipInputUpdateInput}
          />
          <Text small>Third Party Client Names</Text>
        </div>
      );
    };

    const customRenderOTTHubCustomerID = () => {
      return (
        <div style={{ marginBottom: '20px', width: '800px' }}>
          <Row noHorizontalPadding noVerticalPadding>
            <Cols style={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Col>
                <Text small>Liveramp OTT Hub Customer ID</Text>
              </Col>
              <Col>
                <FormBuilderTextInput
                  placeholder={'Enter Liveramp OTT Hub Customer ID'}
                  initialValue={liverampOTTHubCustomerID}
                  handleOnChange={handleLiverampOTTHubCustomerIDChange}
                  key={'1'}
                  inputID={'1'}
                  width={'400px'}
                />
              </Col>
            </Cols>
          </Row>
        </div>
      );
    };

    const customRenderDistributionManagerID = (): JSX.Element => {
      return (
        <div style={{ marginBottom: '20px', width: '800px' }}>
          <Row noHorizontalPadding noVerticalPadding>
            <Cols style={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Col displayFlex>
                <Text style={{ marginRight: '5px' }} small>
                  Distribution Manager ID
                </Text>

                <Tooltip title='Distribution Manager ID can be found in the Destination Account LiveRamp Connect URL (shown as <DM_ID> in this example URL): https://connect.liveramp.com/distribution/accounts/<DM_ID>/segments?customer_id=<ID2>&organization=<ID3>'>
                  <InfoIcon style={{ verticalAlign: 'bottom', width: '1rem', color: '#929599' }} />
                </Tooltip>
              </Col>
              <Col>
                <FormBuilderTextInput
                  placeholder={'Enter Liveramp Distribution Manager ID'}
                  initialValue={liverampDistributionManagerID}
                  handleOnChange={handleLiverampDistributionIDChange}
                  key={'2'}
                  width={'400px'}
                  inputID={'2'}
                />
              </Col>
            </Cols>
          </Row>
        </div>
      );
    };

    const customRenderCadentTag = () => {
      return (
        <div style={{ margin: '20px 0px 20px 0px', width: '800px' }}>
          <Text title>Cadent Settings * </Text>
          <Text muted extraSmall>
            Enter details for Cadent Settings{' '}
          </Text>
          <ChipInput
            fullWidth
            value={cadentTag}
            inputValue={cadentTagInput}
            onAdd={handleChipInputAddCadentTag}
            onDelete={handleChipInputDeleteCadentTag}
            onUpdateInput={handleChipInputUpdateInputCadentTag}
            disabled={cadentEnabled !== EBool.EBool_TRUE}
          />
          <Text small>Cadent Tag</Text>
        </div>
      );
    };

    const customRenderToggle = (partner: string, value: EBool, isDataShare: boolean) => {
      const description = value === EBool.EBool_TRUE ? 'out' : 'in';
      const acceptButtonTitle = value === EBool.EBool_TRUE ? 'Toggle Off' : 'Toggle On';
      const title = isDataShare ? 'Audience Data Share *' : 'Audience Data Storage *';
      return (
        <ConfirmationDialogButton
          title='Confirm Audience Data Share'
          description={`You are opting ${description} to share device ID and IP data with ${partner}. Please confirm`}
          acceptButtonTitle={acceptButtonTitle}
          acceptButtonCallBack={() => {
            handleChangeDataShareToggle(partner, isDataShare);
          }}
          refuseButtonTitle='Cancel'
        >
          <Div
            style={{ display: 'flex', flexDirection: 'row', width: '795px', marginBottom: '10px' }}
          >
            <Div
              style={{
                display: 'flex',
                flexDirection: 'row',
                width: '380px',
                justifyContent: 'space-between',
              }}
            >
              <Text small> {title} </Text>
              <Div style={{ marginTop: '-9px' }}>
                <Switch checked={value === EBool.EBool_TRUE} />
              </Div>
            </Div>
            <Text extraSmall bold>
              {value === EBool.EBool_TRUE ? 'Opt In' : 'Opt Out'}
            </Text>
          </Div>
        </ConfirmationDialogButton>
      );
    };

    const customRenderOTTHubToggle = () => {
      const description = optInSharingDataLiverampOTTHub === EBool.EBool_TRUE ? 'out' : 'in';
      const acceptButtonTitle =
        optInSharingDataLiverampOTTHub === EBool.EBool_TRUE ? 'Toggle Off' : 'Toggle On';

      return (
        <ConfirmationDialogButton
          title='Confirm Audience Data Share'
          description={`You are opting ${description} to share auction level aggregate data with LiveRamp. Please confirm`}
          acceptButtonTitle={acceptButtonTitle}
          acceptButtonCallBack={() => {
            setOptInSharingDataLiverampOTTHub(EBool.reverse(optInSharingDataLiverampOTTHub));
          }}
          refuseButtonTitle='Cancel'
        >
          <Div
            style={{ display: 'flex', flexDirection: 'row', width: '795px', marginBottom: '10px' }}
          >
            <Div
              style={{
                display: 'flex',
                flexDirection: 'row',
                width: '380px',
                justifyContent: 'space-between',
              }}
            >
              <Text small> Enable OTT Hub * </Text>
              <Div style={{ marginTop: '-9px' }}>
                <Switch checked={optInSharingDataLiverampOTTHub === EBool.EBool_TRUE} />
              </Div>
            </Div>
            <Text extraSmall bold>
              {optInSharingDataLiverampOTTHub === EBool.EBool_TRUE
                ? 'Opt In Sharing LiveRamp Data'
                : 'Opt Out Sharing LiveRamp Data'}
            </Text>
          </Div>
        </ConfirmationDialogButton>
      );
    };

    const getLiveRampPanel = (): IAccordionPanel => {
      const LiveRampEnabledRow = {
        renderType: 'SimpleSwitch',
        title1: 'LiveRamp Enabled *',
        value1: liverampEnabled === EBool.EBool_TRUE,
        helperText: liverampEnabled === EBool.EBool_TRUE ? 'Enabled' : 'Disabled',
        handleChange1: () => {
          setUpdatingPublisher(true);
          setLiverampEnabled(EBool.reverse(liverampEnabled));
        },
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const LiveRampAudienceDataShareRow = {
        renderType: 'custom',
        title1: 'Audience Data Share *',
        customRender: () =>
          customRenderToggle(AudienceSegmentThirdPartyPartnerLiveRamp, optInLiveramp, true),
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const LiveRampAudienceDataStorageRow = {
        renderType: 'custom',
        title1: 'Audience Data Storage *',
        customRender: () =>
          customRenderToggle(
            AudienceSegmentThirdPartyPartnerLiveRamp,
            optInStoringDataLiveramp,
            false,
          ),
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const LiveRampFirstPartyRow = {
        renderType: 'custom',
        title1: 'First Party Client Names',
        customRender: () => customRenderFirstPartyClientNames(),
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const LiveRampThirdPartyRow = {
        renderType: 'custom',
        title1: 'Third Party Client Names',
        customRender: () => customRenderThirdPartyClientNames(),
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const LiveRampOTTHubRow = {
        renderType: 'custom',
        title1: 'Liveramp OTT Hub Customer ID',
        customRender: () => customRenderOTTHubCustomerID(),
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const LiveRampDistributionID = {
        renderType: 'custom',
        title1: 'Distribution Manager ID',
        customRender: () => customRenderDistributionManagerID(),
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const LiveRampAudienceDataShareOTTHubRow = {
        renderType: 'custom',
        title1: 'Enable OTT Hub *',
        customRender: () => customRenderOTTHubToggle(),
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const LiveRampPanel = {
        title: 'LiveRamp *  ',
        panelInactiveRowTag: liverampEnabled === EBool.EBool_TRUE ? 'Active' : 'Inactive',
        panelActiveChipTitle: 'Active',
        rows: [
          LiveRampEnabledRow,
          LiveRampAudienceDataShareRow,
          LiveRampAudienceDataStorageRow,
          LiveRampAudienceDataShareOTTHubRow,
          LiveRampFirstPartyRow,
          LiveRampThirdPartyRow,
          LiveRampOTTHubRow,
          LiveRampDistributionID,
        ],
      } as IAccordionPanel;

      return LiveRampPanel;
    };

    const getCadentPanel = (): IAccordionPanel => {
      const CadentEnabledRow = {
        renderType: 'SimpleSwitch',
        title1: 'Cadent Enabled *',
        value1: cadentEnabled === EBool.EBool_TRUE,
        helperText: cadentEnabled === EBool.EBool_TRUE ? 'Enabled' : 'Disabled',
        handleChange1: () => {
          setUpdatingPublisher(true);
          setCadentEnabled(EBool.reverse(cadentEnabled));
        },
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const CadentAudienceDataShareRow = {
        renderType: 'custom',
        title1: 'Audience Data Share *',
        customRender: () =>
          customRenderToggle(AudienceSegmentThirdPartyPartnerCadent, optInCadent, true),
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const CadentAudienceDataStorageRow = {
        renderType: 'custom',
        title1: 'Audience Data Storage *',
        customRender: () =>
          customRenderToggle(AudienceSegmentThirdPartyPartnerCadent, optInStoringDataCadent, false),
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const CadentTagRow = {
        renderType: 'custom',
        title1: 'Cadent Tag',
        customRender: () => customRenderCadentTag(),
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const CadentPanel = {
        title: 'Cadent *  ',
        panelInactiveRowTag: cadentEnabled === EBool.EBool_TRUE ? 'Active' : 'Inactive',
        panelActiveChipTitle: '',
        rows: [
          CadentEnabledRow,
          CadentAudienceDataShareRow,
          CadentAudienceDataStorageRow,
          CadentTagRow,
        ],
      } as IAccordionPanel;

      return CadentPanel;
    };

    const getSamsungDMPPanel = (): IAccordionPanel => {
      const SamsungDMPEnabledRow = {
        renderType: 'SimpleSwitch',
        title1: 'SamsungDMP Enabled *',
        value1: samsungDMPEnabled === EBool.EBool_TRUE,
        helperText: samsungDMPEnabled === EBool.EBool_TRUE ? 'Enabled' : 'Disabled',
        handleChange1: () => {
          setUpdatingPublisher(true);
          setSamsungDMPEnabled(EBool.reverse(samsungDMPEnabled));
        },
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const SamsungDMPPanel = {
        title: 'SamsungDMP *  ',
        panelInactiveRowTag: samsungDMPEnabled === EBool.EBool_TRUE ? 'Active' : 'Inactive',
        panelActiveChipTitle: 'Active',
        rows: [SamsungDMPEnabledRow],
      };
      return SamsungDMPPanel;
    };

    const getOnePlusXDMPPanel = (): IAccordionPanel => {
      const OnePlusXDMPEnabledRow = {
        renderType: 'SimpleSwitch',
        title1: '1PlusX DMP Enabled *',
        value1: onePlusXDMPEnabled === EBool.EBool_TRUE,
        helperText: onePlusXDMPEnabled === EBool.EBool_TRUE ? 'Enabled' : 'Disabled',
        handleChange1: () => {
          setUpdatingPublisher(true);
          setOnePlusXEnabled(EBool.reverse(onePlusXDMPEnabled));
        },
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const OnePlusXDMPPanel = {
        title: '1PlusX DMP  *  ',
        panelInactiveRowTag: onePlusXDMPEnabled === EBool.EBool_TRUE ? 'Active' : 'Inactive',
        panelActiveChipTitle: 'Active',
        rows: [OnePlusXDMPEnabledRow],
      };
      return OnePlusXDMPPanel;
    };

    const getTransunionPanel = (): IAccordionPanel => {
      const TransunionEnabledRow = {
        renderType: 'SimpleSwitch',
        title1: 'Transunion Enabled *',
        value1: transunionEnabled === EBool.EBool_TRUE,
        helperText: transunionEnabled === EBool.EBool_TRUE ? 'Enabled' : 'Disabled',
        handleChange1: () => {
          setUpdatingPublisher(true);
          setTransunionEnabled(EBool.reverse(transunionEnabled));
        },
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const TransunionAudienceDataShareRow = {
        renderType: 'custom',
        title1: 'Audience Data Share *',
        customRender: () =>
          customRenderToggle(AudienceSegmentThirdPartyPartnerTransUnion, optInTransunion, true),
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const TransunionAudienceDataStorageRow = {
        renderType: 'custom',
        title1: 'Audience Data Storage *',
        customRender: () =>
          customRenderToggle(
            AudienceSegmentThirdPartyPartnerTransUnion,
            optInStoringDataTransunion,
            false,
          ),
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const TransunionAPIKeyRow = {
        renderType: 'Textfield',
        enableSmallRow: true,
        title1: 'API Key *',
        value1: transunionAPIKey,
        handleChange1: ({ target }: React.ChangeEvent<HTMLInputElement>) => {
          setUpdatingPublisher(true);
          setTransunionAPIKey(target.value);
        },
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      const TransunionPanel = {
        title: 'Transunion *  ',
        panelInactiveRowTag: transunionEnabled === EBool.EBool_TRUE ? 'Active' : 'Inactive',
        panelActiveChipTitle: 'Active',
        rows: [
          TransunionEnabledRow,
          TransunionAudienceDataShareRow,
          TransunionAudienceDataStorageRow,
          TransunionAPIKeyRow,
        ],
      } as IAccordionPanel;

      return TransunionPanel;
    };

    const LiveRampPanel = getLiveRampPanel();
    const CadentPanel = getCadentPanel();
    const TransunionPanel = getTransunionPanel();
    const SamsungDMPPanel = getSamsungDMPPanel();
    const OnePlusXDMPPanel = getOnePlusXDMPPanel();

    const panels = [
      LiveRampPanel,
      CadentPanel,
      TransunionPanel,
      SamsungDMPPanel,
      OnePlusXDMPPanel,
    ] as IAccordionPanel[];

    return (
      <Panels maxWidth vertical relative minWidthMain>
        <Panel
          largeHorizontalPadding
          verticalPadding
          style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
        >
          <ModelTopBar
            title={routeName(pathname)}
            session={session}
            user={user}
            publisher={session.publisher}
            classes={classes}
            delegateElevate={true}
            currentPath={pathname}
            disableTitlePaddingLeft
          />
        </Panel>
        <Panel scroll style={{ height: '100vh' }}>
          <Div
            smallVerticalPadding
            hugeHorizontalPadding
            style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Cols>
              <Col>
                <ButtonView
                  onClick={handleSave}
                  text='Save'
                  active={false}
                  disabled={!updatingPublisher}
                />
              </Col>
              <Col smallHorizontalPadding>
                <ModelSecondaryButton onClick={gotoAudienceManagerSettings}>
                  Close
                </ModelSecondaryButton>
              </Col>
            </Cols>
          </Div>
          <Panel extraLargeHorizontalPadding style={{ width: '920px', marginBottom: '24px' }}>
            <MultiAccordion title='Data Providers' panels={panels} disableEdit={false} />
          </Panel>
        </Panel>
        <SnackbarView open={!!snackbarMessage} error={!!snackbarError} onClose={snackbarClose}>
          {snackbarMessage}
        </SnackbarView>
      </Panels>
    );
  },
);
